import React, { useState } from 'react';

/**
 * <StatCard label="Credit Balance" value={1337} sub="available" accent="var(--accent)" icon={<Icon .../>}/>
 *
 * Refined enterprise stat card — clean hierarchy, subtle hover.
 */
export default function StatCard({
  label,
  value,
  sub,
  accent = 'var(--accent)',
  icon,
  trend,
  style,
  ...rest
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: `1px solid ${hover ? 'var(--border-bright)' : 'var(--border-subtle)'}`,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        transition: 'transform var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? 'var(--shadow-md), var(--inset-top)' : 'var(--shadow-sm), var(--inset-top)',
        ...style,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      {/* Thin top accent line */}
      <div
        aria-hidden
        style={{
          height: 2,
          background: `linear-gradient(90deg, ${accent} 0%, transparent 70%)`,
          opacity: 0.8,
        }}
      />

      <div style={{ padding: '16px 18px 16px' }}>
        {/* Label row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--sp-3)',
        }}>
          <span style={{
            fontSize: 'var(--fs-2xs)',
            fontWeight: 'var(--fw-bold)',
            color: 'var(--text-muted)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}>
            {label}
          </span>
          {icon && (
            <div
              aria-hidden
              style={{
                width: 28, height: 28,
                borderRadius: 7,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                background: `${accent}12`,
                color: accent,
                opacity: hover ? 1 : 0.85,
                transition: 'opacity var(--dur-fast)',
              }}
            >
              {icon}
            </div>
          )}
        </div>

        {/* Value */}
        <div style={{
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-bold)',
          lineHeight: 1.1,
          letterSpacing: '-0.03em',
          color: 'var(--text-primary)',
          marginBottom: 8,
        }}>
          {value ?? '—'}
        </div>

        {/* Sub + trend */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}>
          {sub && (
            <span style={{
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)',
            }}>
              {sub}
            </span>
          )}
          {trend && (
            <span style={{
              fontSize: 'var(--fs-2xs)',
              fontWeight: 'var(--fw-semi)',
              fontFamily: 'var(--font-mono)',
              color: trend.up ? 'var(--success)' : 'var(--danger)',
              flexShrink: 0,
            }}>
              {trend.up ? '↑' : '↓'} {trend.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
